import { useCallback, useEffect, useState } from "react";
import { listCuratedVoices } from "@/services/ads/videos/videoDraftService";
import type { CuratedVoice } from "@/services/ads/videos/videoDraft.types";

// Phase 2 V2 — Session-scoped cache of the curated voice catalog. The
// list is static-per-deploy on the backend (hardcoded), so pulling it
// more than once per session is wasted bandwidth. A module-level cache
// survives component unmounts but refreshes on a full page reload, which
// matches how product ships deploys.
let cachedVoices: CuratedVoice[] | null = null;
let inFlight: Promise<CuratedVoice[] | null> | null = null;

function normalizeVoices(voices: CuratedVoice[] | null | undefined): CuratedVoice[] | null {
  const geminiVoices = (voices ?? []).filter((voice) => voice.provider === "gemini");
  return geminiVoices.length > 0 ? geminiVoices : null;
}

async function fetchOnce(): Promise<CuratedVoice[] | null> {
  if (cachedVoices) {
    const normalized = normalizeVoices(cachedVoices);
    if (normalized) return normalized;
    cachedVoices = null;
  }
  if (inFlight) return inFlight;
  inFlight = listCuratedVoices().then(({ res }) => {
    cachedVoices = normalizeVoices(res);
    inFlight = null;
    return cachedVoices;
  });
  return inFlight;
}

export interface UseCuratedVoicesResult {
  voices: CuratedVoice[];
  isLoading: boolean;
  error: boolean;
  refresh: () => void;
}

export function useCuratedVoices(enabled: boolean = true): UseCuratedVoicesResult {
  const [voices, setVoices] = useState<CuratedVoice[]>(cachedVoices ?? []);
  const [isLoading, setIsLoading] = useState(!cachedVoices && enabled);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    const result = await fetchOnce();
    if (result) {
      setVoices(result);
    } else {
      setError(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (cachedVoices) {
      setVoices(cachedVoices);
      setIsLoading(false);
      return;
    }
    load();
  }, [enabled, load]);

  const refresh = useCallback(() => {
    cachedVoices = null;
    inFlight = null;
    load();
  }, [load]);

  return { voices, isLoading, error, refresh };
}
